/**
 * AdminLayout - Main Layout Component
 * 
 * Sidebar navigation, header with user info, and content outlet
 */

import React, { useState } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAdminAuth } from '../auth/useAdminAuth.js';

export const AdminLayout = () => {
  const { user, logout } = useAdminAuth();
  const navigate = useNavigate();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('[AdminLayout] Logout failed:', error);
    }
  };

  const toggleSidebar = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  // Navigation items
  const navItems = [
    { path: '/', label: 'Dashboard', icon: '📊', superAdminOnly: false },
    { path: '/users', label: 'Users', icon: '👥', superAdminOnly: false },
    { path: '/email-tracking', label: 'Email Tracking', icon: '📧', superAdminOnly: false },
    { path: '/subscriptions', label: 'Subscriptions', icon: '💳', superAdminOnly: false },
    { path: '/payments', label: 'Payments', icon: '💰', superAdminOnly: false },
    { path: '/promo-codes', label: 'Promo Codes', icon: '🎟️', superAdminOnly: false },
    { path: '/promo-analytics', label: 'Promo Analytics', icon: '📊', superAdminOnly: true },
    { path: '/promo-usage', label: 'Promo Usage', icon: '🔍', superAdminOnly: false },
    { path: '/webhooks', label: 'Webhooks', icon: '🔗', superAdminOnly: false },
    { path: '/analytics', label: 'Analytics', icon: '📈', superAdminOnly: false },
    { path: '/ai-config', label: 'AI Configuration', icon: '🤖', superAdminOnly: true },
    { path: '/system', label: 'System', icon: '⚙️', superAdminOnly: false },
    { path: '/health', label: 'Health', icon: '❤️', superAdminOnly: false },
    { path: '/audit-logs', label: 'Audit Logs', icon: '📝', superAdminOnly: true },
    { path: '/tickets', label: 'Support Tickets', icon: '🎫', superAdminOnly: false },
    { path: '/content', label: 'Content', icon: '📚', superAdminOnly: false },
    { path: '/feature-flags', label: 'Feature Flags', icon: '🚩', superAdminOnly: false },
    { path: '/monitoring', label: 'Monitoring', icon: '📡', superAdminOnly: false },
    { path: '/notifications', label: 'Notifications', icon: '🔔', superAdminOnly: false },
    { path: '/exports', label: 'Data Export', icon: '📤', superAdminOnly: false },
    { path: '/abuse-detection', label: 'Abuse Detection', icon: '🛡️', superAdminOnly: false },
    { path: '/tts', label: 'TTS Generator', icon: '🎤', superAdminOnly: false },
  ];

  // Filter nav items based on user role
  const visibleNavItems = navItems.filter(item => 
    !item.superAdminOnly || user?.isSuperAdmin
  );

  return (
    <div className={`admin-layout ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
      {/* Sidebar */}
      <aside className="admin-sidebar">
        <div className="admin-sidebar__header">
          <div className="admin-logo">
            <span className="admin-logo__icon">🔐</span>
            {!sidebarCollapsed && (
              <span className="admin-logo__text">EduQ Admin</span>
            )}
          </div>
          {!sidebarCollapsed && (
            <button
              className="admin-sidebar__toggle"
              onClick={toggleSidebar}
              aria-label="Collapse sidebar"
            >
              ◀
            </button>
          )}
        </div>

        <nav className="admin-nav">
          {visibleNavItems.map(item => (
            <NavLink
              key={item.path}
              to={item.path}
              className={({ isActive }) =>
                `admin-nav__item ${isActive ? 'admin-nav__item--active' : ''}`
              }
              end={item.path === '/'}
            >
              <span className="admin-nav__icon">{item.icon}</span>
              {!sidebarCollapsed && (
                <span className="admin-nav__label">{item.label}</span>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="admin-sidebar__footer">
          <NavLink
            to="https://eduq-ai.com/app"
            className="admin-nav__item admin-back-to-app"
            target="_blank"
            rel="noopener noreferrer"
          >
            <span className="admin-nav__icon">🏠</span>
            {!sidebarCollapsed && (
              <span className="admin-nav__label">Back to App</span>
            )}
          </NavLink>
        </div>
      </aside>

      {/* Main Content */}
      <main className="admin-main">
        {/* Header */}
        <header className="admin-header">
          <div className="admin-header__left">
            {sidebarCollapsed && (
              <button
                className="admin-sidebar__toggle"
                onClick={toggleSidebar}
                aria-label="Expand sidebar"
                style={{ 
                  background: 'var(--bg-secondary)', 
                  color: 'var(--text-primary)',
                  marginRight: 'var(--space-4)'
                }}
              >
                ▶
              </button>
            )}
            <h1 className="admin-page-title">Admin Portal</h1>
          </div>

          <div className="admin-header__right">
            <div className="admin-user-info">
              <span className="admin-user-info__email">{user?.email}</span>
              <span className={`admin-user-info__badge ${
                user?.isSuperAdmin 
                  ? 'admin-user-info__badge--super' 
                  : 'admin-user-info__badge--admin'
              }`}>
                {user?.isSuperAdmin ? 'SuperAdmin' : 'Admin'}
              </span>
            </div>
            <button
              className="admin-logout-btn"
              onClick={handleLogout}
            >
              Logout
            </button>
          </div>
        </header>

        {/* Content Area */}
        <div className="admin-content">
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default AdminLayout;
